import useScheduleRealtime from '../../realtime/useScheduleRealtime';
import React, { useState, useRef, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import Table from "react-bootstrap/Table";
import Modal from "react-bootstrap/Modal";
import Alert from "react-bootstrap/Alert";
import Spinner from "react-bootstrap/Spinner";
import EditJobModal from "./EditJobModal";
import { hasAnyEditPermission } from './utils/schedulePermissions';
import { fetchData } from '../../redux/slices/apiCallSlice';
import { selectApiUrls } from '../../redux/slices/apiUrlSlice';
import { getProjectId } from '../../utils/dataNormalization';
import SortableTableHeader from "./SortableTableHeader";
import { getNextSortConfig, sortTableRows } from "./utils/tableSorting";

const TABLE_COLUMNS = [
  { key: "JobNumber", label: "Job Number", type: "text" },
  { key: "ProjectName", label: "Project Name", type: "text" },
  { key: "ModelNumber", label: "Model Number", type: "text" },
  { key: "OrderDate", label: "Order Date", type: "date" },
  { key: "ProductionRelease", label: "Release To Scheduling", type: "date" },
  { key: "RequestedDeliveryDate", label: "Requested Delivery Date", type: "date" },
  { key: "SalesRep", label: "Sales Rep", type: "text" },
  { key: "State", label: "State", type: "text" },
  { key: "JobType", label: "Job Type", type: "text" },
  { key: "Notes", label: "Notes", type: "text" },
];

const CanceledJobs = ({ filteredJobs = [], canEdit = true }) => {
  const dispatch = useDispatch();
  // Use filteredJobs prop if available, otherwise fall back to Redux store
  const jobScheduleData = useSelector((state) => state.jobSchedule.data);
  const apiUrls = useSelector(selectApiUrls);
  const [canceledApiJobs, setCanceledApiJobs] = useState([]);
  const [hasLoadedApiJobs, setHasLoadedApiJobs] = useState(false);
  const [loadingApiJobs, setLoadingApiJobs] = useState(false);
  const [apiJobsError, setApiJobsError] = useState(null);
  
  // Get user roles for permission checking
  const userRoles = useSelector((state) => state.user?.userRoles) || [];
  const userRolesMapped = useSelector((state) => state.user?.rolesMapped) || {};
  const canEditAccess = Boolean(canEdit) && hasAnyEditPermission(userRoles);
  
  const [showModal, setShowModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [visibleRows, setVisibleRows] = useState(50); // Initial number of rows to display
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "ascending",
  });
  const containerRef = useRef(null);

  useScheduleRealtime({ view: 'canceled', blocked: loadingApiJobs || showModal, apply: rows => { setCanceledApiJobs(rows); setHasLoadedApiJobs(true); setApiJobsError(null); } });

  useEffect(() => {
    let isMounted = true;

    const loadCanceledJobs = async () => {
      if (!apiUrls?.fullSchedule) {
        return;
      }

      setLoadingApiJobs(true);
      setApiJobsError(null);

      try {
        const queryJoiner = apiUrls.fullSchedule.includes("?") ? "&" : "?";
        const url = `${apiUrls.fullSchedule}${queryJoiner}jobStatus=${encodeURIComponent('canceled')}`;
        const response = await dispatch(fetchData({ url })).unwrap();
        const raw = response?.data || response || {};
        const normalizedJobs = (Array.isArray(raw)
          ? raw
          : Object.values(raw)
              .map((item) => item?.GeneralProjectInfo)
              .filter(Boolean)
        ).filter((job, index, arr) =>
          index === arr.findIndex((entry) => getProjectId(entry) === getProjectId(job))
        );

        if (isMounted) {
          setCanceledApiJobs(normalizedJobs);
          setHasLoadedApiJobs(true);
        }
      } catch (error) {
        console.error('CanceledJobs: Failed to load jobs from full schedule endpoint:', error);
        if (isMounted) {
          setApiJobsError('Failed to load canceled jobs.');
          setHasLoadedApiJobs(true);
        }
      } finally {
        if (isMounted) {
          setLoadingApiJobs(false);
        }
      }
    };

    loadCanceledJobs();

    return () => {
      isMounted = false;
    };
  }, [dispatch, apiUrls?.fullSchedule]);

  const handleRowDoubleClick = (job) => {
    // Disable double-click functionality for accounting users (canceled jobs are read-only for accounting)
    if (userRolesMapped && userRolesMapped.Accounting && !userRolesMapped.Admin && !userRolesMapped.Scheduling) {
      return; // Do nothing for accounting users
    }
    
    // Only allow double-click functionality if user has edit permissions
    if (canEditAccess) {
      setSelectedJob(job);
      setShowModal(true);
    }
    // Do nothing if the user doesn't have permissions
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedJob(null);
  };

  const handleScroll = () => {
    if (containerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
      if (scrollTop + clientHeight >= scrollHeight - 50) {
        setVisibleRows((prev) => prev + 50); // Load 50 more rows
      }
    }
  };

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
      return () => container.removeEventListener("scroll", handleScroll);
    }
  }, []);

  const dataToUse = (() => {
    const baseData = hasLoadedApiJobs
      ? canceledApiJobs
      : (Array.isArray(jobScheduleData) ? jobScheduleData : []);

    if (!Array.isArray(filteredJobs) || filteredJobs.length === 0) {
      return baseData;
    }

    const filteredIds = new Set(filteredJobs.map((job) => getProjectId(job)));
    return baseData.filter((job) => filteredIds.has(getProjectId(job)));
  })();

  const filteredCanceledJobs = dataToUse;

  const sortedCanceledJobs = useMemo(
    () => sortTableRows(filteredCanceledJobs, TABLE_COLUMNS, sortConfig),
    [filteredCanceledJobs, sortConfig]
  );
  const visibleData = sortedCanceledJobs.slice(0, visibleRows);

  const handleSort = (key) => {
    setSortConfig((currentSort) => getNextSortConfig(currentSort, key));
    setVisibleRows(50);
    if (containerRef.current) {
      containerRef.current.scrollTop = 0;
    }
  };

  return (
    <div
      className="canceled-jobs-container"
      style={{ overflow: "auto", maxHeight: "80vh" }}
      ref={containerRef}
    >
      {loadingApiJobs && (
        <div style={{ textAlign: "center", padding: "20px" }}>
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading canceled jobs...</span>
          </Spinner>
          <p>Loading canceled jobs...</p>
        </div>
      )}

      {apiJobsError && filteredCanceledJobs.length === 0 && (
        <Alert variant="danger">{apiJobsError}</Alert>
      )}

      <Table striped bordered hover responsive className="schedule-responsive-table">
        <SortableTableHeader
          columns={TABLE_COLUMNS}
          sortConfig={sortConfig}
          onSort={handleSort}
        />
        <tbody>
          {visibleData.length > 0 ? (
            visibleData.map((job) => (
              <tr
                key={job.ID}
                onDoubleClick={() => handleRowDoubleClick(job)}
                style={{ cursor: (canEditAccess && !(userRolesMapped && userRolesMapped.Accounting && !userRolesMapped.Admin && !userRolesMapped.Scheduling)) ? 'pointer' : 'default' }}
                title={(canEditAccess && !(userRolesMapped && userRolesMapped.Accounting && !userRolesMapped.Admin && !userRolesMapped.Scheduling)) ? "Double-click to edit" : ""}
              >
                <td data-label="Job Number">{job.JobNumber}</td>
                <td data-label="Project Name">{job.ProjectName}</td>
                <td data-label="Model Number">{job.ModelNumber}</td>
                <td data-label="Order Date">{job.OrderDate ? new Date(job.OrderDate).toLocaleDateString() : ""}</td>
                <td data-label="Production Release">{job.ProductionRelease ? new Date(job.ProductionRelease).toLocaleDateString() : ""}</td>
                <td data-label="Requested Delivery">{job.RequestedDeliveryDate ? new Date(job.RequestedDeliveryDate).toLocaleDateString() : ""}</td>
                <td data-label="Sales Rep">{job.SalesRep}</td>
                <td data-label="State">{job.State}</td>
                <td data-label="Job Type">{job.JobType}</td>
                <td data-label="Notes">{job.Notes}</td>
                {canEditAccess && !(userRolesMapped && userRolesMapped.Accounting && !userRolesMapped.Admin && !userRolesMapped.Scheduling) && (
                  <td data-label="Actions" className="schedule-card-action-cell">
                    <button type="button" className="btn btn-primary schedule-card-action" onClick={() => handleRowDoubleClick(job)}>
                      Open job
                    </button>
                  </td>
                )}
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={TABLE_COLUMNS.length} className="schedule-empty-row">No data available</td>
            </tr>
          )}
        </tbody>
      </Table>
      {visibleRows < sortedCanceledJobs.length && (
        <div style={{ textAlign: "center", padding: "10px" }}>
          Loading more rows...
        </div>
      )}
      <Modal className="job-schedule-modal" show={showModal} onHide={handleCloseModal} size="xl">
        <EditJobModal
          selectedJob={selectedJob}
          setSelectedJob={setSelectedJob}
          handleCloseModal={handleCloseModal}
          handleSaveChanges={() => {}}
          userRoles={userRoles}
        />
      </Modal>
    </div>
  );
};

export default CanceledJobs;
